import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Plus, Send, CheckCircle2, XCircle, Loader2, RefreshCw, Clock, Phone, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  confirmed: { label: 'Confirmada', cls: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/25' },
  cancelled: { label: 'Cancelada',  cls: 'text-red-400 bg-red-500/10 border-red-500/25' },
  completed: { label: 'Realizada',  cls: 'text-slate-400 bg-slate-500/10 border-slate-500/25' },
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

function NewEventModal({ onClose, onSave }) {
  const [form, setForm] = useState({ contact_name: '', contact_phone: '', title: '', date_iso: '', duration_min: 30, notes: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = (e) => { e.preventDefault(); if (!form.contact_name || !form.date_iso) return; onSave(form); };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
        className="glass-card w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-text-heading font-semibold">Nueva Cita</h3>
          <button onClick={onClose}><X className="w-4 h-4 text-text-muted" /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {[['Nombre del contacto *', 'contact_name', 'text'], ['Teléfono WA', 'contact_phone', 'text'], ['Título de la cita *', 'title', 'text']].map(([label, key, type]) => (
            <div key={key}>
              <label className="text-[11px] text-text-muted uppercase tracking-wider">{label}</label>
              <input type={type} value={form[key]} onChange={e => set(key, e.target.value)}
                className="w-full mt-1 bg-background border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-body focus:outline-none focus:border-primary" />
            </div>
          ))}
          <div>
            <label className="text-[11px] text-text-muted uppercase tracking-wider">Fecha y hora *</label>
            <input type="datetime-local" value={form.date_iso} onChange={e => set('date_iso', e.target.value)}
              className="w-full mt-1 bg-background border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-body focus:outline-none focus:border-primary" />
          </div>
          <div>
            <label className="text-[11px] text-text-muted uppercase tracking-wider">Duración (min)</label>
            <select value={form.duration_min} onChange={e => set('duration_min', Number(e.target.value))}
              className="w-full mt-1 bg-background border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-body focus:outline-none focus:border-primary">
              {[15, 30, 45, 60, 90].map(d => <option key={d} value={d}>{d} min</option>)}
            </select>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary !py-2 text-sm">Cancelar</button>
            <button type="submit" className="flex-1 btn-primary !py-2 text-sm">Guardar cita</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

export default function AgenteAgendaPanel({ authFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [remindingId, setRemindingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [tab, setTab] = useState('upcoming');

  const apiFn = authFetch || ((url, opts) => fetch(url, { ...opts, credentials: 'include' }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFn(`${API}/agenda-agent/events`);
      setData(await res.json());
    } catch { toast.error('Error al cargar agenda'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAgent = async () => {
    setRunning(true);
    try {
      const res = await apiFn(`${API}/agenda-agent/run`, { method: 'POST' });
      const d = await res.json();
      toast.success(d.message || 'Agente ejecutado');
      await load();
    } catch { toast.error('Error al ejecutar agente'); }
    finally { setRunning(false); }
  };

  const sendReminder = async (eventId) => {
    setRemindingId(eventId);
    try {
      const res = await apiFn(`${API}/agenda-agent/remind/${eventId}`, { method: 'POST' });
      const d = await res.json();
      if (d.wa_result === 'enviado') toast.success('Recordatorio WA enviado');
      else toast.warning(`WA: ${d.wa_result}`);
      await load();
    } catch { toast.error('Error'); }
    finally { setRemindingId(null); }
  };

  const updateStatus = async (eventId, status) => {
    try {
      await apiFn(`${API}/agenda-agent/events/${eventId}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status }) });
      toast.success('Estado actualizado');
      await load();
    } catch { toast.error('Error'); }
  };

  const createEvent = async (form) => {
    try {
      await apiFn(`${API}/agenda-agent/events`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      toast.success('Cita creada');
      setShowModal(false);
      await load();
    } catch { toast.error('Error al crear cita'); }
  };

  const events = tab === 'upcoming' ? (data?.upcoming || []) : (data?.past || []);
  const stats = data?.stats || {};

  return (
    <div className="space-y-5" data-testid="agente-agenda-panel">
      {showModal && <AnimatePresence><NewEventModal onClose={() => setShowModal(false)} onSave={createEvent} /></AnimatePresence>}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-text-heading font-heading text-lg font-semibold flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" /> Agente de Agenda
          </h3>
          <p className="text-sm text-text-muted mt-0.5">Reservas automáticas desde el pipeline + recordatorios WA.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={loading} className="btn-secondary !px-3 !py-2 text-xs">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={runAgent} disabled={running} className="btn-secondary !px-4 !py-2 text-sm flex items-center gap-1.5">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
            Importar del pipeline
          </button>
          <button onClick={() => setShowModal(true)} className="btn-primary !px-4 !py-2 text-sm flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Nueva cita
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total citas', value: stats.total ?? 0, color: 'text-primary' },
          { label: 'Próximas', value: stats.upcoming ?? 0, color: 'text-blue-400' },
          { label: 'Sin recordatorio', value: stats.pending_reminder ?? 0, color: 'text-amber-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="glass-card p-4" data-testid={`agenda-stat-${label}`}>
            <p className="text-[10px] text-text-muted uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-2xl font-black ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border-subtle pb-0">
        {[['upcoming', 'Próximas'], ['past', 'Historial']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${tab === id ? 'border-primary text-primary' : 'border-transparent text-text-muted hover:text-text-body'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* Event list */}
      <div className="space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-text-muted"><Loader2 className="w-5 h-5 animate-spin mr-2" />Cargando…</div>
        ) : events.length === 0 ? (
          <div className="text-center py-10 text-text-muted text-sm">Sin citas en esta sección</div>
        ) : (
          events.map(ev => {
            const st = STATUS_STYLES[ev.status] || STATUS_STYLES.confirmed;
            return (
              <motion.div key={ev.event_id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="glass-card p-4 flex flex-wrap items-center gap-4" data-testid={`agenda-event-${ev.event_id}`}>
                <div className="flex-1 min-w-0 space-y-0.5">
                  <p className="text-sm font-semibold text-text-heading">{ev.title}</p>
                  <p className="text-xs text-text-muted flex items-center gap-1.5">
                    <Clock className="w-3 h-3" /> {fmtDate(ev.date_iso)} · {ev.duration_min} min
                  </p>
                  {ev.contact_phone && <p className="text-xs text-text-muted flex items-center gap-1.5"><Phone className="w-3 h-3" /> {ev.contact_name} · {ev.contact_phone}</p>}
                </div>
                <span className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
                {ev.status === 'confirmed' && (
                  <div className="flex gap-2">
                    <button onClick={() => sendReminder(ev.event_id)} disabled={remindingId === ev.event_id}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-primary/15 text-primary border border-primary/30 hover:bg-primary/25 transition-colors disabled:opacity-50"
                      data-testid={`agenda-remind-${ev.event_id}`}>
                      {remindingId === ev.event_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                      WA
                    </button>
                    <button onClick={() => updateStatus(ev.event_id, 'completed')} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/25 hover:bg-emerald-500/20 transition-colors" data-testid={`agenda-complete-${ev.event_id}`}>
                      <CheckCircle2 className="w-3 h-3" />
                    </button>
                    <button onClick={() => updateStatus(ev.event_id, 'cancelled')} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/25 hover:bg-red-500/20 transition-colors" data-testid={`agenda-cancel-${ev.event_id}`}>
                      <XCircle className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}
